import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { ShieldCheck } from '@phosphor-icons/react';

export default function ForcePasswordChange() {
  const { setUser, user, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (newPw.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPw === currentPw) {
      toast.error('New password must be different from current password');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success('Password updated — welcome!');
      // Optimistically clear the flag so the user continues without a full reload
      const updated = { ...user, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="force-password-change"
      className="fixed inset-0 z-[9999] bg-[#0A0A0A]/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="bg-white max-w-md w-full p-6 sm:p-8 border border-[#E5E5E5] shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
            <ShieldCheck size={22} weight="bold" />
          </div>
          <div>
            <h2 className="text-xl font-black tracking-tight text-[#0A0A0A]">Set a new password</h2>
            <p className="text-xs text-[#666] mt-0.5">You are using a temporary password. Please choose a new one to continue.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <div>
            <Label className="text-xs uppercase tracking-widest">Current password</Label>
            <Input
              data-testid="fpc-current-pw"
              type="password"
              value={currentPw}
              onChange={e => setCurrentPw(e.target.value)}
              required
              className="rounded-none"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">New password (min 6 chars)</Label>
            <Input
              data-testid="fpc-new-pw"
              type="password"
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              required
              minLength={6}
              className="rounded-none"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Confirm new password</Label>
            <Input
              data-testid="fpc-confirm-pw"
              type="password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              required
              minLength={6}
              className="rounded-none"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              data-testid="fpc-submit"
              type="submit"
              disabled={submitting}
              className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none flex-1"
            >
              {submitting ? 'Updating...' : 'Update password'}
            </Button>
            <Button
              data-testid="fpc-logout"
              type="button"
              variant="outline"
              onClick={logout}
              className="rounded-none"
            >
              Sign out
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
